import { Box, LinearProgress, Paper, Typography, alpha } from "@mui/material";
import { Check, NotificationsNoneOutlined } from "@mui/icons-material";
import { Badge, Callout, PageHeader } from "@components/ui_v2";

/** Estado inicial del diseño (`S_GenContratos`): dos pasos listos, el tercero en curso y la barra al 55%. */
const PASO_ACTUAL = 2;
const PROGRESO = 55;

const PASOS = [
  { label: "Expediente autorizado", sub: "Ratificación crediticia y legal completa" },
  { label: "Datos sincronizados", sub: "Información validada de empresa, aval y propietario real" },
  { label: "Generando documentos contractuales", sub: "Estamos preparando 8 documentos para firma" },
  { label: "Validación notarial", sub: "Verificación legal automática" },
  { label: "Listo para firmar", sub: "Te avisaremos cuando podamos firmar" },
];

type ItemState = "done" | "running" | "pending";

const getState = (idx: number): ItemState => {
  if (idx < PASO_ACTUAL) return "done";
  if (idx === PASO_ACTUAL) return "running";
  return "pending";
};

/** Marca circular del timeline (`.ld-tl-mark`): mismo spec que en `WaitingRoomTimeline`. */
const TimelineMark = ({ state }: { state: ItemState }) => (
  <Box
    sx={{
      width: 28,
      height: 28,
      borderRadius: "50%",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      position: "relative",
      zIndex: 1,
      border: "2px solid",
      ...(state === "done" && {
        bgcolor: "success.main",
        borderColor: "success.main",
        color: "#fff",
      }),
      ...(state === "running" && {
        bgcolor: "primary.main",
        borderColor: "primary.main",
        color: "#fff",
        boxShadow: (theme) => `0 0 0 4px ${alpha(theme.palette.primary.main, 0.18)}`,
        "@keyframes ldTlPulse": {
          "0%": { transform: "scale(0.85)", opacity: 0.5 },
          "100%": { transform: "scale(1.4)", opacity: 0 },
        },
        "&::after": {
          content: '""',
          position: "absolute",
          inset: "-6px",
          borderRadius: "50%",
          border: "2px solid",
          borderColor: "primary.main",
          opacity: 0.35,
          animation: "ldTlPulse 1.6s ease-out infinite",
        },
      }),
      ...(state === "pending" && {
        bgcolor: "background.paper",
        borderColor: "divider",
        color: "text.disabled",
      }),
    }}
  >
    {state === "done" && <Check sx={{ fontSize: 15 }} />}
    {state === "running" && <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#fff" }} />}
  </Box>
);

interface TimelineItemProps {
  state: ItemState;
  label: string;
  sub: string;
}

/** Fila del timeline (`.ld-tl-item`); en esta pantalla la pastilla de los pasos hechos dice "Listo". */
const TimelineItem = ({ state, label, sub }: TimelineItemProps) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, py: "12px", position: "relative" }}>
    <TimelineMark state={state} />
    <Box sx={{ flex: 1, pt: "4px" }}>
      <Typography
        sx={{
          fontSize: 14,
          letterSpacing: "-0.005em",
          fontWeight: state === "pending" ? 500 : 600,
          color: state === "pending" ? "text.secondary" : "text.primary",
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontSize: 12, color: "text.secondary", mt: "2px" }}>{sub}</Typography>
    </Box>
    {state === "running" && <Badge tone="info">En curso</Badge>}
    {state === "done" && <Badge tone="ok">Listo</Badge>}
  </Box>
);

/**
 * Sala de espera de la generación de contratos (`S_GenContratos` del diseño). Solo maquetado: muestra el estado
 * inicial fijo; el botón "Revisar y firmar contratos" del diseño aparece hasta que todos los pasos terminan.
 */
export const GeneracionContratos = () => (
  <Box>
    <PageHeader
      eyebrow="Formalización · Generación de contratos"
      title="Estamos preparando tus contratos"
      subtitle="Generamos los documentos contractuales con los datos validados de tu expediente. Este proceso suele tardar entre 2 y 5 minutos."
    />

    <Paper variant="lendia_paper" sx={{ p: "28px" }}>
      <LinearProgress
        variant="determinate"
        value={PROGRESO}
        sx={{
          mb: "22px",
          height: 4,
          borderRadius: 999,
          bgcolor: (theme) => theme.palette.tokens?.neutralSoftBg,
          "& .MuiLinearProgress-bar": { borderRadius: 999 },
        }}
      />

      {/* Timeline con línea conectora vertical — spec `.ld-timeline` del diseño. */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            left: "13px",
            top: "16px",
            bottom: "16px",
            width: "2px",
            borderRadius: "1px",
            bgcolor: "divider",
          },
        }}
      >
        {PASOS.map((paso, i) => (
          <TimelineItem key={paso.label} state={getState(i)} label={paso.label} sub={paso.sub} />
        ))}
      </Box>

      <Box sx={{ mt: "24px" }}>
        <Callout tone="info" icon={<NotificationsNoneOutlined sx={{ fontSize: 14 }} />}>
          <strong>¡Despreocúpate!</strong> Puedes salir de esta pantalla. En cuanto los contratos estén listos te
          contactaremos por <strong>correo y WhatsApp</strong> para que regreses a firmar.
        </Callout>
      </Box>
    </Paper>
  </Box>
);
